"use client";

import React from "react";
import { useTranslation } from "react-i18next";
import { useForm, Controller } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@components/ui/dialog";
import { zodResolver } from "@hookform/resolvers/zod";
import { Input } from "@components/ui/input";
import { Label } from "@components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@components/ui/select";
import { RootState } from "@store/store";
import {
  createCustomerProfile,
} from "@store/features/missingCustomerSlice";
import { MissingCustomerSchema } from "../auth/MissingCustomerSchema";
import { format } from "date-fns";
import {

  Switch as KeepSwitch,
  Spinner,
} from "keep-react";
import {
  selectBrandId,
  selectBrandString,
  selectPrivacyLink,
  selectTermsLink,
  selectLoyaltyLink,
} from "@store/features/styleSchemaSlice";
import { useStyleSchema } from "@components/styleSchema/StyleSchemaHandler";
import clsx from "clsx";
import { CountrySelect } from "@/components/ui/CountrySelect";
import { SteppedBirthdayInput } from "@/components/ui/SteppedBirthdayInput";
import { getCountryByCode } from "@/lib/constants/countries";
import DOMPurify from 'dompurify';

/**
 * Form data interface for the missing customer profile completion form.
 * Used when a user needs to complete their profile to activate their account.
 */
interface MissingCustomerFormData {
  /** User's salutation (Mr./Mrs.) */
  salutation: string;
  /** User's first name */
  firstName: string;
  /** User's last name */
  lastName: string;
  /** User's email address (usually pre-filled from localStorage) */
  email: string;
  /** Optional phone number */
  telefon?: string;
  /** Street address including house number */
  streetAndHouseNumber: string;
  /** Postal/ZIP code */
  postalCode: string;
  /** City name */
  city: string;
  /** Country code (ISO 2-letter format) */
  country: string;
  /** User's date of birth */
  dateOfBirth: Date | undefined;
  /** Whether user accepts marketing communications */
  marketingAccepted: boolean;
}

/**
 * Missing Customer Form Component
 * 
 * A modal form that allows users to complete their customer profile when their account
 * exists but is missing required information for full activation. This typically happens
 * during account activation when additional data is needed.
 * 
 * Features:
 * - Pre-filled email from localStorage (readonly)
 * - Stepped birthday input for better UX
 * - Address validation with country restrictions (EU/EEA only)
 * - Marketing consent options
 * - Real-time form validation
 * 
 * @returns The missing customer form modal component
 */
export const MissingCustomerForm: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<any>();
  const brandId = useSelector(selectBrandId);
  const { currentSchema } = useStyleSchema();
  const buttonColor = currentSchema.colors?.primary?.hex;

  const { isModalOpen, isSubmitting } = useSelector(
    (state: RootState) => state.missingCustomer
  );

  const [userEmail, setUserEmail] = React.useState<string>("");
  const brandString = useSelector(selectBrandString);
  const privacyUrl = useSelector(selectPrivacyLink);
  const termsUrl = useSelector(selectTermsLink);
  const loyaltyUrl = useSelector(selectLoyaltyLink);

  const schema = React.useMemo(() => MissingCustomerSchema(t), [t]);

  // Formular initialisieren
  const {
    handleSubmit,
    control,
    formState: { errors, isValid },
    setValue,
    trigger,
    getValues,
  } = useForm<MissingCustomerFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      salutation: t("missingCustomer.form.mr"),
      firstName: "",
      lastName: "", 
      email: userEmail || "",
      telefon: "",
      streetAndHouseNumber: "",
      postalCode: "",
      city: "",
      country: "DE",
      dateOfBirth: undefined,
      marketingAccepted: false,
    },
    mode: "all",
    reValidateMode: "onChange",
    criteriaMode: "all",
  });


  
  /**
   * Effect to load email from localStorage and set it in the form
   * The email is typically stored during the login/registration process
   */
  React.useEffect(() => {
    const storedEmail = localStorage.getItem("mail") || "";
    if (storedEmail) {
      setUserEmail(storedEmail);
      setValue("email", storedEmail);
      // Trigger validation after setting the value
      setTimeout(() => trigger(), 100);
    }
  }, [setValue, trigger]);

  /**
   * Additional effect to revalidate form when all fields might be filled
   * This ensures proper validation state updates
   */
  React.useEffect(() => {
    const timer = setTimeout(() => {
      trigger();
    }, 500);
    return () => clearTimeout(timer);
  }, [trigger]);

  /**
   * Handles form submission and creates customer profile
   * Validates form data and sends it to the backend API
   */
  const handleFormSubmit = async () => {
    const isValid = await trigger();
    
    if (!isValid) {
      console.error('Form contains validation errors');
      return;
    }
    
    const formData = getValues();
    
    try {
      // Transform country code to German name for backend compatibility
      const countryName = getCountryByCode(formData.country)?.nameDE || formData.country;
      
      await dispatch(
        createCustomerProfile({
          email: formData.email,
          anrede: formData.salutation === t("missingCustomer.form.mr") ? "1" : "2",
          vorname: formData.firstName,
          nachname: formData.lastName,
          telefon: formData.telefon || "",
          strasse_und_hausnummer: formData.streetAndHouseNumber,
          adresszusatz: "",
          plz: formData.postalCode,
          ort: formData.city,
          country: countryName,
          geburtsdatum: formData.dateOfBirth
            ? format(formData.dateOfBirth, "dd.MM.yyyy")
            : "",
          // Values for bonus card creation
          marketing: formData.marketingAccepted ? 1 : 0,
          trigger_create_card: true,
          brand_create_card: brandId ? brandId.toString() : "",
          kundennummer: '',
        })
      );      
    } catch (error) {
      console.error("Error creating customer profile:", error);
    }
  };
  
  /**
   * Form submission handler for the onSubmit event
   * Logs the form data and triggers the main submission function
   */
  const onSubmit = (data: MissingCustomerFormData) => {
    console.log('Form submitted with data:', data);
    handleFormSubmit();
  };

  return (
    <Dialog open={isModalOpen} onOpenChange={() => {}} modal>
      <DialogContent className="bg-white md:max-w-3xl max-h-[90vh] overflow-y-auto mx-auto w-[90%] rounded-lg p-4 [&>button]:hidden">
        <DialogHeader className="px-6 pt-6 pb-0">
          <DialogTitle className="text-xl font-semibold">
            {t("missingCustomer.title")}
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            {t("missingCustomer.description")}
          </DialogDescription>
        </DialogHeader>

        <div className="relative w-full">
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="space-y-6 px-6 py-4 w-full"
          >
            {/* Personal Information Section */}
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Salutation */}
                <div>
                  <Label htmlFor="salutation">
                    {t("missingCustomer.form.salutation")}
                    <span className="text-red-500 ml-0.5">*</span>
                  </Label>
                  <Controller
                    name="salutation"
                    control={control}
                    render={({ field }) => (
                      <Select
                        onValueChange={(value) => {
                          field.onChange(value);
                          trigger('salutation');
                        }}
                        value={field.value}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue
                            placeholder={t("missingCustomer.form.salutation")}
                          />
                        </SelectTrigger>
                        <SelectContent className="bg-white">
                          <SelectItem value={t("missingCustomer.form.mr")}>
                            {t("missingCustomer.form.mr")}
                          </SelectItem>
                          <SelectItem value={t("missingCustomer.form.mrs")}>
                            {t("missingCustomer.form.mrs")}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  />
                </div>

                {/* First Name */}
                <div>
                  <Label htmlFor="firstName">
                    {t("missingCustomer.form.firstName")}
                    <span className="text-red-500 ml-0.5">*</span>
                  </Label>
                  <Controller
                    name="firstName"
                    control={control}
                    render={({ field }) => (
                      <Input
                        id="firstName"
                        {...field}
                        className={`mt-1 ${
                          errors.firstName ? "border-red-500" : ""
                        }`}
                        placeholder={t("register.placeholders.firstName")}
                      />
                    )}
                  />
                  {errors.firstName && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.firstName.message}
                    </p>
                  )}
                </div>

                {/* Last Name */}
                <div>
                  <Label htmlFor="lastName">
                    {t("missingCustomer.form.lastName")}
                    <span className="text-red-500 ml-0.5">*</span>
                  </Label>
                  <Controller
                    name="lastName"
                    control={control}
                    render={({ field }) => (
                      <Input
                        id="lastName"
                        {...field}
                        className={`mt-1 ${
                          errors.lastName ? "border-red-500" : ""
                        }`}
                        placeholder={t("register.placeholders.lastName")}
                      />
                    )}
                  />
                  {errors.lastName && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.lastName.message}
                    </p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Date of Birth */}
                <div>
                  <SteppedBirthdayInput
                    name="dateOfBirth"
                    control={control}
                    label={t("missingCustomer.form.dateOfBirth")}
                    required={true}
                    error={errors.dateOfBirth?.message}
                  />
                </div>

                {/* Phone Number */}
                <div>
                  <Label htmlFor="telefon">
                    {t("missingCustomer.form.telefon")}
                  </Label>
                  <Controller
                    name="telefon"
                    control={control}
                    render={({ field }) => (
                      <Input
                        id="telefon"
                        type="tel"
                        {...field}
                        className="mt-1"
                        placeholder={t("register.placeholders.telefon")}
                      />
                    )}
                  />
                </div>
              </div>
            </div>

                          {/* Address Information Section */}
              <div className="space-y-4">
                {/* Street and House Number */}
              <div>
                <Label htmlFor="streetAndHouseNumber">
                  {t("missingCustomer.form.streetAndHouseNumber")}
                  <span className="text-red-500 ml-0.5">*</span>
                </Label>
                <Controller
                  name="streetAndHouseNumber"
                  control={control}
                  render={({ field }) => (
                    <Input
                      id="streetAndHouseNumber"
                      {...field}
                      className={`mt-1 ${
                        errors.streetAndHouseNumber ? "border-red-500" : ""
                      }`}
                      placeholder={t("register.placeholders.streetAndHouseNumber")}
                    />
                  )}
                />
                {errors.streetAndHouseNumber && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.streetAndHouseNumber.message}
                  </p>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {/* Postal Code */}
                <div className="col-span-1">
                  <Label htmlFor="postalCode">
                    {t("missingCustomer.form.postalCode")}
                    <span className="text-red-500 ml-0.5">*</span>
                  </Label>
                  <Controller
                    name="postalCode"
                    control={control}
                    render={({ field }) => (
                      <Input
                        id="postalCode"
                        {...field}
                        className={`mt-1 ${
                          errors.postalCode ? "border-red-500" : ""
                        }`}
                        placeholder={t("register.placeholders.postalCode")}
                      />
                    )}
                  />
                  {errors.postalCode && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.postalCode.message}
                    </p>
                  )}
                </div>

                {/* City */}
                <div className="col-span-3">
                  <Label htmlFor="city">
                    {t("missingCustomer.form.city")}
                    <span className="text-red-500 ml-0.5">*</span>
                  </Label>
                  <Controller
                    name="city"
                    control={control}
                    render={({ field }) => (
                      <Input
                        id="city"
                        {...field}
                        className={`mt-1 ${
                          errors.city ? "border-red-500" : ""
                        }`}
                        placeholder={t("register.placeholders.city")}
                      />
                    )}
                  />
                  {errors.city && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.city.message}
                    </p>
                  )}
                </div>
              </div>

              <div>
                <CountrySelect
                  name="country"
                  control={control}
                  label={t("missingCustomer.form.country")}
                  placeholder={t("register.placeholders.country")}
                  required={true}
                  defaultValue="DE"
                  error={errors.country?.message}
                />
              </div>

              {/* Email Address */}
              <div>
                <Label htmlFor="email">
                  {t("register.labels.email")}
                  <span className="text-red-500 ml-0.5">*</span>
                </Label>
                <Controller
                  name="email"
                  control={control}
                  render={({ field }) => (
                    <Input
                      id="email"
                      {...field}
                      readOnly={!!userEmail}
                      className={`mt-1 ${userEmail ? 'bg-gray-100' : ''}`}
                      placeholder={t("register.placeholders.email")}
                    />
                  )}
                />
              </div>
            </div>

            {/* Marketing and Privacy Section */}
            <div className="space-y-4">
              <div className="space-y-3 mt-2">
                {/* Marketing Consent */}
                <div className="flex flex-col">
                  <div className="flex items-center gap-4">
                    <Controller
                      name="marketingAccepted"
                      control={control}
                      render={({ field }) => (
                        <div>
                          <KeepSwitch
                            checked={field.value}
                            onCheckedChange={(checked) => {
                              field.onChange(checked);
                            }}
                            className="bg-primary"
                          />
                        </div>
                      )}
                    />
                    <p
                      className="flex-1 text-sm font-medium text-gray-700"
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(t("register.marketing.label", {
                          privacyLink: `<a class="text-[var(--accent-color)] hover:underline" href="${loyaltyUrl}" target="_blank">`,
                          "/privacyLink": "</a>",
                        })),
                      }}
                    />
                  </div>
                </div>

                {/* Privacy Policy and Terms */}
                <div className="flex flex-col">
                  <div className="flex items-center gap-4">
                    <div>
                      <KeepSwitch checked={true} className="bg-primary" />
                    </div>
                    <div className="flex items-start">
                      <span className="text-red-500">*</span>
                      <p
                        className="flex-1 text-sm font-medium text-gray-700"
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(t("register.terms.label", {
                            termsLink: `<a class="text-[var(--accent-color)] hover:underline" href="${termsUrl}" target="_blank">`,
                            "/termsLink": "</a>",
                            privacyLink: `<a class="text-[var(--accent-color)] hover:underline" href="${privacyUrl}" target="_blank">`,
                            "/privacyLink": "</a>",
                            brandString,
                          })),
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="w-full flex justify-center mt-8 gap-4">
              <button
                type="button"
                onClick={handleFormSubmit}
                disabled={isSubmitting || !isValid}
                className={clsx(
                  "px-4 py-2 rounded text-center md:w-auto disabled:opacity-50 disabled:cursor-not-allowed",
                  {
                    "flex items-center gap-2": isSubmitting,
                  }
                )}
                style={{
                  background: buttonColor,
                  color: "white",
                }}
              >
                {isSubmitting ? (
                  <>
                    <Spinner color="primary" />
                    <span className="mr-2">
                      {t("missingCustomer.submittingButton")}
                    </span>
                  </>
                ) : (
                  t("missingCustomer.submitButton")
                )}
              </button>
            </div>
            <p className="text-sm text-slate-600">{t("app.requiredFields")}</p>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
};
